/**
 * Deterministic Amazon ASIN extraction. (POR-13612-2)
 *
 * Creative Studio accepts a product/reference identifier as a bare ASIN
 * (B + 9 alphanumeric). Customers also paste a FULL Amazon product URL, which
 * carries the ASIN after `/dp/` (or `/gp/product/`). This normalizes either form
 * to the bare ASIN so every ASIN entry point supports full URLs too, instead of
 * relying on the LLM to parse the URL (which produced "Got <the whole URL>").
 *
 * Mirror of the backend App\Domains\Auth\Services\Networks\Support\AmazonUrl
 * (kept in sync; the PHP side carries the unit tests).
 *
 * @param {string|null|undefined} input
 * @returns {string|null} the uppercased ASIN, or null if none is found.
 */
export function extractAsin(input) {
  if (input == null) return null;
  const s = String(input).trim();
  if (s === '') return null;

  // Bare ASIN: the whole string is an ASIN (B + 9 alphanumeric).
  const bare = s.match(/^(B[A-Z0-9]{9})$/i);
  if (bare) return bare[1].toUpperCase();

  // Beyond a bare ASIN, only an Amazon URL carries one.
  if (!/amazon\./i.test(s)) return null;

  // Sponsored (sspa) links wrap the real product URL, URL-encoded, in a `url=`
  // param — decode it and pull the ASIN from the /dp/ inside.
  if (/sspa\/click/i.test(s)) {
    const u = s.match(/[?&]url=([^&]+)/i);
    if (u) {
      let decoded = u[1];
      try { decoded = decodeURIComponent(u[1]); } catch (e) { /* keep raw */ }
      const m = decoded.match(/\/(?:dp|gp\/product)\/(B[A-Z0-9]{9})(?![A-Z0-9])/i);
      if (m) return m[1].toUpperCase();
    }
  }

  // Path forms: /dp/, /gp/product/, /gp/aw/d/, /exec/obidos/ASIN/, /product-reviews/.
  const path = s.match(/\/(?:dp|gp\/product|gp\/aw\/d|exec\/obidos\/ASIN|product-reviews)\/(B[A-Z0-9]{9})(?![A-Z0-9])/i);
  if (path) return path[1].toUpperCase();

  // Query param: ?ASIN=… / &asin=…
  const query = s.match(/[?&]asin=(B[A-Z0-9]{9})(?![A-Z0-9])/i);
  if (query) return query[1].toUpperCase();

  return null;
}

/**
 * Return the Amazon marketplace domain (e.g. `amazon.com.mx`, `amazon.co.uk`,
 * `amazon.com`) from a full Amazon URL, or null for a bare ASIN / product name /
 * non-Amazon URL. ASINs are marketplace-specific, so the image scraper must hit
 * the storefront the URL came from; callers default to `amazon.com` when null.
 *
 * Mirror of App\Domains\Auth\Services\Networks\Support\AmazonUrl::extractMarketplaceDomain.
 *
 * @param {string|null|undefined} input
 * @returns {string|null}
 */
export function extractMarketplaceDomain(input) {
  if (input == null) return null;
  const s = String(input).trim();
  if (s === '' || !/amazon\./i.test(s)) return null;

  const m = s.match(/amazon\.[a-z]{2,3}(?:\.[a-z]{2})?/i);
  return m ? m[0].toLowerCase() : null;
}

/**
 * Human-readable primary language of an Amazon marketplace (e.g. "Spanish"),
 * derived from a full URL or a bare `amazon.<tld>` domain. Used to force the
 * generated PDP copy into the ORIGINAL product's language (a .com.mx product must
 * read in Spanish even when styled after an English reference). null when unknown.
 *
 * Mirror of App\Domains\Auth\Services\Networks\Support\AmazonUrl::marketplaceLanguage
 * (the PHP side carries the unit tests). (POR-13612-3)
 *
 * @param {string|null|undefined} input
 * @returns {string|null}
 */
export function marketplaceLanguage(input) {
  const domain = extractMarketplaceDomain(input);
  if (domain == null) return null;

  const languages = {
    'amazon.com': 'English',
    'amazon.co.uk': 'English',
    'amazon.ca': 'English',
    'amazon.ie': 'English',
    'amazon.in': 'English',
    'amazon.com.au': 'English',
    'amazon.sg': 'English',
    'amazon.ae': 'English',
    'amazon.com.mx': 'Spanish',
    'amazon.es': 'Spanish',
    'amazon.com.br': 'Portuguese',
    'amazon.de': 'German',
    'amazon.fr': 'French',
    'amazon.it': 'Italian',
    'amazon.nl': 'Dutch',
    'amazon.com.be': 'Dutch',
    'amazon.se': 'Swedish',
    'amazon.pl': 'Polish',
    'amazon.com.tr': 'Turkish',
    'amazon.co.jp': 'Japanese',
    'amazon.sa': 'Arabic',
    'amazon.eg': 'Arabic',
  };

  return languages[domain] || null;
}
